import axios from 'axios';
import { PDFDocument, StandardFonts, rgb, degrees, PDFName, PDFDict, PDFRef } from 'pdf-lib';
import { v4 as uuidv4 } from 'uuid';
import iconSignlyOld from '../assets/iconSignlyold.png';

// Constants
import { typesSign, SIGNATURE_GEOMETRY, PDFJS_SAFE_OPTIONS } from '@/constants';
import { isMobileLayout } from '@/hooks/useIsMobileLayout';

// Externals
import { googleLogout } from '@react-oauth/google';
import { pdfjs } from 'react-pdf';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

// Config de invitación (allowDownload/allowView/ignore) cacheada por documento.
// Centralizado aquí porque el mismo JSON.parse sin protección estaba repetido
// en 5 archivos distintos: si el valor guardado no es JSON válido, en vez de
// tronar el render/handler donde se leía, se limpia y se sigue con {}.
export const getStoredInvitationConfig = (documentId) => {
  try {
    return JSON.parse(localStorage.getItem(`invitationConfig_${documentId}`)) || {};
  } catch (error) {
    console.error('invitationConfig corrupto en localStorage, se limpiará:', error);
    localStorage.removeItem(`invitationConfig_${documentId}`);
    return {};
  }
};

export const generateId = () => {
  const randomNumber = Math.random().toString();
  return randomNumber.substring(2);
};

export const validateEmail = (email = '') => {
  if (
    !email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line
    ) ||
    email.length < 1
  ) {
    return false;
  }
  return true;
};

export const indexedParticipants = (participants) => {
  const newParticipants = participants.reduce(
    (acc, el) => ({
      ...acc,
      [el.email]: el
    }),
    {}
  );
  return newParticipants;
};

export const indexedParticipantsBySign = (participants) => {
  const newParticipants = participants.reduce(
    (acc, el) => ({
      ...acc,
      [el.typeofSign]: el
    }),
    {}
  );
  return newParticipants;
};

// const getHeaders = (requireToken, isFormData) => {
//   const token = Cookies.get('token') || sessionStorage.getItem('tokenInvite');
//   const headers = {
//     Accept: 'application/json',
//     'Access-Control-Allow-Origin': '*'
//   };
//   if (requireToken) {
//     headers['Authorization'] = token;
//   }
//   if (isFormData) {
//     headers['Content-Type'] = 'multipart/form-data';
//   } else {
//     headers['Content-Type'] = 'application/json';
//   }
//   return headers;
// };

const getHeaders = (requireToken, isFormData) => {
  const headers = {
    Accept: 'application/json'
  };

  if (requireToken) {
    const tokenInvite = sessionStorage.getItem('tokenInvite');
    if (tokenInvite && window.location.pathname.startsWith('/shared')) {
      headers['Authorization'] = tokenInvite;
    }
    // En muchos casos ya no es necesario establecer el header 'Authorization'
    // porque el token httpOnly se envía automáticamente.
  }

  headers['Content-Type'] = isFormData
    ? 'multipart/form-data'
    : 'application/json';

  return headers;
};

export const makeRequest = async(
  url,
  method,
  data,
  isFormData = false,
  requireToken = true,
  responseType = null,
  onUploadProgress = null,
  timeout = 120000 // 120 segundos por defecto
) => {
  const baseUrl = process.env.REACT_APP_BASE_URL + url;

  // Configuración inicial para axios
  const config = {
    url: baseUrl,
    method,
    data: isFormData ? data : JSON.stringify(data),
    headers: getHeaders(requireToken, isFormData),
    responseType,
    withCredentials: true,
    timeout // Establecer el tiempo de espera
  };

  // Agregar seguimiento del progreso si se proporciona la función
  if (onUploadProgress) {
    config.onUploadProgress = onUploadProgress;
  }

  const { data: responseData } = await axios(config);
  return responseData;
};

// export const makeRequest = async (
//   url,
//   method,
//   data,
//   isFormData = false,
//   requireToken = true,
//   responseType = null
// ) => {
//   try {
//     // Crear una instancia de axios con configuración base
//     const axiosInstance = axios.create({
//       baseURL: process.env.REACT_APP_BASE_URL,
//       headers: getHeaders(requireToken, isFormData),
//     });

//     // Realizar la solicitud y esperar la respuesta
//     const response = await axiosInstance({
//       url,
//       method,
//       data: data !== undefined ? (isFormData ? data : JSON.stringify(data)) : undefined,
//       responseType,
//     });

//     // Devolver los datos de la respuesta directamente
//     return response.data;
//     } catch (error) {
//       // Verificar si el error tiene una respuesta del servidor
//       if (error.response) {
//         // Personalizar el mensaje de error usando los detalles de la respuesta del servidor
//         const errorMessage = error.response.data.message || "Ocurrió un error al procesar tu solicitud.";
//         const errorCode = error.response.status;

//         // Modificar el objeto de error original de Axios
//         error.customError = true; // Una marca para identificar errores personalizados
//         error.status = errorCode; // Conservar el código de estado HTTP
//         error.message = errorMessage; // Actualizar el mensaje de error

//         // Extender el error con cualquier propiedad adicional de error.response.data
//         Object.assign(error, error.response.data);

//       } else {
//         // Manejar errores que no provienen de una respuesta HTTP
//         error.customError = true;
//         error.status = 500; // Un código de estado genérico para errores no HTTP
//         error.message = error.message || "Ocurrió un error de red o desconocido.";
//       }

//       // Lanzar el error modificado
//       console.error("Error en la solicitud:", error);
//       throw error;
//     }
// };

export const getPosY = (
  currentPos,
  heightSelect,
  heightPaint,
  heightElement = 0
) => {
  const posicitionReverse = heightSelect - currentPos;
  const equalPostition = Math.round(
    (posicitionReverse * heightPaint) / heightSelect
  );
  return equalPostition - heightElement;
};

export const getPosX = (currentPos, widthSelect, widthPaint) => {
  const escaleX = widthPaint / widthSelect;
  const equalPostition = currentPos * escaleX;
  return equalPostition;
};

export const dataURItoBlob = (dataURI) => {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

const MOBILE_BREAKPOINT = 768;

export const pdfStamps = async(
  pdfDoc,
  positions,
  participantsIndexed,
  fileName,
  dimenssions,
  documentInfo,
  hasExistingSignatureLines = false
) => {
  // Iteramos sobre las páginas definidas en positions
  for (const signImg in positions) {
    // Aseguramos que exista un array, y filtramos posibles elementos undefined
    const pagePositions = (positions[signImg] || []).filter(p => p);
    if (pagePositions.length === 0) {
      console.warn(`No hay firma asignada en la página ${signImg}, se omite en la generación.`);
      continue;
    }

    const pages = pdfDoc.getPages();
    const selectedPage = pages[parseInt(signImg, 10) - 1];
    const { height, width } = selectedPage.getSize();

    // Creamos un canvas para medir el texto (solo necesitamos measureText)
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    pagePositions.forEach((pos) => {
      if (typeof pos.x !== 'number' || typeof pos.y !== 'number') {
        console.warn(`Posición inválida en la página ${signImg}:`, pos);
        return;
      }

      const nameParticipant = participantsIndexed[pos.email]?.name || '';
      const lastNameParticipant = participantsIndexed[pos.email]?.last_name || '';
      const texto = `${nameParticipant} ${lastNameParticipant}`;
      const medida = context.measureText(texto);
      const adjust = 200 - medida.width;
      const halfAdjust = adjust / 2;

      // Posición exacta SIN offsets extra:
      const Rwidth = mapX(pos.x, documentInfo.width, width);

      // pos.y es el TOP del elemento DraggableLineOfSignature en unidades PDF.
      // La línea visible (borderBottom) está en el BOTTOM del elemento.
      // El alto del elemento es (lineHeightBase + lineExtraBase) unidades PDF — CONSTANTE, zoom-independiente:
      //   Mobile: 10 + 10 = 20 PDF units
      //   Desktop: 16 + 15 = 31 PDF units
      // Misma definición de "layout mobile" que usa la UI (DocumentPdf/Sign.jsx), para que el
      // alto de la línea al estampar coincida con el que se vio al colocarla.
      const isMobileStamp = isMobileLayout();
      const elementHeightPdf = isMobileStamp ? 20 : 31;

      // heightElement = 1, para alinear la línea de 1 unidad de alto
      const Rheight = mapY(pos.y + elementHeightPdf, documentInfo.height, height, 1);

      // NO dibujar la línea gráfica si el documento YA tiene líneas de firma
      if (!hasExistingSignatureLines) {
        // Dibujamos la línea
        selectedPage.drawRectangle({
          x: Rwidth,
          y: Rheight,
          width: 200,
          height: 1
        });
        // Y el texto centrado sobre ella
        selectedPage.drawText(texto, {
          x: Rwidth + halfAdjust,
          y: Rheight - 14,
          size: 12
        });
      }
    });
  }

  const modifiedPdfBytes = await pdfDoc.save();
  const file = new File([modifiedPdfBytes], fileName, {
    type: 'application/pdf'
  });

  return file;
};

export const removeOwner = (participants = [], owner) => {
  const newParticipants = participants.filter((p) => p.email !== owner);
  return newParticipants;
};

export const moreParticipants = (participants = [], owner) => {
  const newParticipants = participants.filter((p) => p.email !== owner);
  return newParticipants.length > 0;
};

export const getDeviceInfo = () => {
  const ua = navigator.userAgent || ''; // Cadena del navegador

  // 1) Detectar sistema operativo
  let os = 'Desconocido';
  if (/Windows NT/i.test(ua)) {
    os = 'Windows';
  } else if (/Macintosh/i.test(ua) && !/iPhone|iPad|iPod/i.test(ua)) {
    os = 'MacOS';
  } else if (/Linux/i.test(ua) && !/Android/i.test(ua)) {
    os = 'Linux';
  } else if (/Android/i.test(ua)) {
    os = 'Android';
  } else if (/iPhone|iPad|iPod/i.test(ua)) {
    os = 'iOS';
  }

  // 2) Detectar tipo de dispositivo: Desktop, Tablet o Mobile
  //    - Si en el userAgent aparece "Mobi", normalmente es teléfono (Mobile).
  //    - Si aparece "Tablet" o “iPad” (iOS), es tablet.
  //    - En caso contrario, se considera Desktop.
  let deviceType = 'Desktop';
  if (/\bMobi/i.test(ua)) {
    deviceType = 'Mobile';
  } else if (/\bTablet|iPad\b/i.test(ua)) {
    deviceType = 'Tablet';
  }

  // 3) Combinar en un solo string, por ejemplo: "iOS/Mobile" o "Windows/Desktop"
  return `${os}/${deviceType}`;
};

function base64ToArrayBuffer(base64) {
  try {
    // Verificar si la cadena Base64 es válida
    if (!base64 || typeof base64 !== 'string' || !base64.includes(',')) {
      console.error('La cadena Base64 no tiene el formato esperado:', base64);
      return null;
    }

    // Dividir el encabezado y la parte codificada
    const base64Data = base64.split(',')[1];

    if (!base64Data) {
      console.error('La parte codificada de la cadena Base64 está vacía o indefinida.');
      return null;
    }

    // Decodificar Base64 a una cadena binaria
    const binaryString = atob(base64Data);

    // Crear un Uint8Array para convertir la cadena binaria en un buffer
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Devolver el ArrayBuffer resultante
    return bytes.buffer;
  } catch (error) {
    console.error('Error al convertir Base64 a ArrayBuffer:', error);
    return null;
  }
}

/**
 * Convierte X de unidades PDF → unidades de página de pdf-lib
 */
export const mapX = (pdfX, pdfDocWidth, pageWidth) => {
  return (pdfX * pageWidth) / pdfDocWidth;
};

/**
 * Convierte Y de unidades PDF → unidades de página de pdf-lib
 * (recuerda que pdf-lib usa origen en bottom-left)
 */
export const mapY = (pdfY, pdfDocHeight, pageHeight, elementHeight = 0) => {
  // 1) Invertimos el eje Y (pdf-lib usa origen bottom-left)
  const inverted = pdfDocHeight - pdfY;
  // 2) Escalamos al tamaño real de la página
  const scaled = (inverted * pageHeight) / pdfDocHeight;
  // 3) Restamos la altura del elemento para que su esquina superior coincida
  return scaled - elementHeight;
};

// La firma dibujada es el lienzo completo de CanvasSign (ancho x 40vh x devicePixelRatio):
// en un iPhone ~1000x1000 px. pdf-lib (embedPng) decodifica y recomprime esa imagen
// completa en CADA hoja firmada, aunque en el PDF solo se dibuja a 190x60 pt. Con
// "Agregar a todas las páginas" son 11 veces ~1 Mpx en el hilo principal: en iPhone la
// pantalla se quedaba en "Actualizando documento" tanto tiempo que el invitado recargaba
// antes de que saliera la petición final. Se reduce UNA vez, antes del loop, al tamaño
// real de dibujo a 3x; como el PDF ya la estiraba a 190x60, el resultado visual no cambia.
// Si algo falla o tarda, se usa la imagen original (sin agregar esperas sin límite).
const SIGNATURE_RASTER_WIDTH = 570; // 190 pt * 3
const SIGNATURE_RASTER_HEIGHT = 180; // 60 pt * 3
const SIGNATURE_RESIZE_TIMEOUT_MS = 3000;

const resizeSignatureForPdf = (dataUrl) => new Promise((resolve) => {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/png;base64,')) {
    resolve(dataUrl);
    return;
  }
  let settled = false;
  const finish = (value) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    resolve(value);
  };
  const timer = setTimeout(() => finish(dataUrl), SIGNATURE_RESIZE_TIMEOUT_MS);
  try {
    const img = new Image();
    img.onload = () => {
      try {
        if (img.naturalWidth <= SIGNATURE_RASTER_WIDTH && img.naturalHeight <= SIGNATURE_RASTER_HEIGHT) {
          finish(dataUrl);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = SIGNATURE_RASTER_WIDTH;
        canvas.height = SIGNATURE_RASTER_HEIGHT;
        canvas.getContext('2d').drawImage(img, 0, 0, SIGNATURE_RASTER_WIDTH, SIGNATURE_RASTER_HEIGHT);
        const resized = canvas.toDataURL('image/png');
        finish(resized && resized.startsWith('data:image/png;base64,') ? resized : dataUrl);
      } catch (error) {
        console.warn('No se pudo reducir la firma; se usa la original:', error);
        finish(dataUrl);
      }
    };
    img.onerror = () => finish(dataUrl);
    img.src = dataUrl;
  } catch (error) {
    console.warn('No se pudo reducir la firma; se usa la original:', error);
    finish(dataUrl);
  }
});

export const savePdfSigns = async(
  positions,
  signsSelected,
  pdfDoc,
  fileName,
  dimenssions,
  country,
  userId,
  userExist,
  documentInfo,
  isMobile,
  hasExistingSignatureLines
) => {
  if (!signsSelected?.img || signsSelected.img === 'data:image/png;base64,') {
    const validationError = new Error('Firma inválida: No se ha capturado la firma.');
    // Validación de negocio esperada (el usuario dio clic en "firmar" sin antes
    // capturar/seleccionar su firma) — no es un bug. Misma convención que
    // isExpectedError en useMakeRequest.jsx: los catch que reportan a Sentry
    // revisan esta bandera para no alertar por algo que el toast ya le indicó
    // al usuario.
    validationError.isExpectedError = true;
    throw validationError;
  }
  const date = new Date().toLocaleString();
  // console.log('userId en helpers:', userId);

  // Log adicional para todas las posiciones de las firmas
  console.log('Firmas a procesar:');
  for (const signImg in positions) {
    if (positions[signImg] && positions[signImg].length > 0) {
      const position = positions[signImg][0];
      // console.log(`Página: ${signImg}, X: ${position.x}, Y: ${position.y}, UserId: ${userId}`);
    } else {
      console.warn(`Página: ${signImg} no tiene firma asignada.`);
    }
  }

  // Ícono de marca de agua para invitados sin cuenta Signly: antes se descargaba
  // (fetch de red) UNA VEZ POR CADA PÁGINA firmada, sin timeout ni manejo de error —
  // en una red móvil real, si esa descarga se colgaba en cualquier página, todo el
  // proceso de firma quedaba esperando para siempre (loader "Actualizando documento"
  // infinito) sin que el backend recibiera nunca la petición final, y sin ningún
  // rastro en Sentry (un fetch que nunca resuelve no lanza ninguna excepción que
  // capturar). Confirmado reproduciendo el bug real con esta misma condición.
  // El ícono es la misma imagen estática en todas las páginas, así que se descarga
  // e incrusta UNA sola vez aquí (con timeout), y luego solo se estampa (drawImage)
  // en cada página dentro del loop — el resultado visual no cambia, sigue
  // apareciendo en cada hoja firmada.
  let iconImageEmbed = null;
  if (!userExist) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const iconResponse = await fetch(iconSignlyOld, { signal: controller.signal });
      const iconBytes = await iconResponse.arrayBuffer();
      iconImageEmbed = await pdfDoc.embedPng(iconBytes);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Firma reducida a su tamaño real de dibujo (ver resizeSignatureForPdf) e incrustada
  // UNA sola vez, igual que el ícono: antes se volvía a decodificar e incrustar en cada
  // hoja, así que el tiempo y el peso del PDF crecían con el número de hojas (100 hojas
  // ≈ 16 veces más lento y 7 veces más KB que incrustarla una vez). La marca que valida
  // el backend (Signature_<userId>_<pageIndex> con su BBox) es un diccionario aparte que
  // se crea en cada hoja más abajo, no esta imagen, así que compartirla no la afecta.
  const signatureImgForPdf = await resizeSignatureForPdf(signsSelected.img);
  const signatureImageEmbed = await pdfDoc.embedPng(base64ToArrayBuffer(signatureImgForPdf));

  // Procesar cada página
  for (const signImg in positions) {
    // Si no hay firma asignada en esta página, la omitimos
    if (!(positions[signImg] && positions[signImg].length > 0)) {
      console.warn(`No hay firma asignada en la página ${signImg}, se omite en la generación.`);
      continue;
    }

    const pages = pdfDoc.getPages();
    // Convertir signImg a número de página (restando 1 para el índice cero)
    const pageIndex = parseInt(signImg, 10) - 1;
    const selectedPage = pages[pageIndex];
    const { height, width } = selectedPage.getSize();

    const offsetX = 10; // Offset a la derecha
    const signatureBBoxWidth = 200;
    // Usar la primera posición ya validada
    const posData = positions[signImg][0];

    // tamaños *reales* del PDF (no del canvas)
    const pdfDocWidth = documentInfo.width; // Ancho original del PDF
    const pdfDocHeight = documentInfo.height;// Alto original del PDF

    // tamaño de la página en unidades de pdf-lib
    const { width: pageWidth, height: pageHeight } = selectedPage.getSize();

    // calculamos el mismo offsetY de Sign.jsx pero en unidades PDF (constante, zoom-independiente):
    // Sign.jsx: offsetY = elementHeightPdf * scaleY - blinkHeight * scaleY  (no-existing lines)
    //         = (elementHeightPdf - blinkHeightPdf) * scaleY
    // En PDF units (÷ scaleY): offsetYPdf = elementHeightPdf - blinkHeightPdf
    // Sign.jsx: blinkHeight = (isMobile ? 60 : 65) * scaleY  → blinkHeightPdf = isMobile ? 60 : 65
    const blinkHeightPdf = isMobile ? 60 : 65;
    // Desktop: lineHeightBase(16) + lineExtraBase(15) = 31 | Mobile: 10 + 10 = 20
    const elementHeightPdf_stamp = hasExistingSignatureLines
      ? (isMobile ? 20 : 16)
      : (isMobile ? 20 : 31);
    // Con líneas existentes (selector) el offset es FIJO y no depende del dispositivo de quien firma:
    // antes, en mobile se usaba 80 en vez de 55.5 y la firma quedaba ~25 pt más abajo que en desktop.
    // Es el mismo valor que usan el recuadro de Sign.jsx y la caja del dueño (constants/signatureGeometry.js).
    const offsetYPdf = hasExistingSignatureLines
      ? SIGNATURE_GEOMETRY.SIMPLE_EXISTING_LINES.offsetY
      : elementHeightPdf_stamp - blinkHeightPdf;

    // mapeamos de coordenadas “PDF” → coordenadas de página
    const Rwidth = mapX(
      posData.x,
      pdfDocWidth,
      pageWidth
    );

    const yConOffset = posData.y + offsetYPdf;

    const sHeight = 60;
    // Y recuerda invertir el eje Y porque pdf-lib usa origen abajo-izquierda
    const Rheight = mapY(
      yConOffset,
      pdfDocHeight,
      pageHeight,
      sHeight
      /* opcional: getAdjust(pageHeight) si lo necesitas para tu ajuste */
    );

    // Ajustar si la firma se saldría por la derecha
    let adjustedRwidth = Rwidth;
    if (adjustedRwidth + offsetX + signatureBBoxWidth > width) {
      adjustedRwidth = width - signatureBBoxWidth - offsetX;
    }
    if (adjustedRwidth < 0) {
      adjustedRwidth = 0;
    }

    const rotation = posData.rotation !== undefined ? -posData.rotation : 0;
    console.log('rotation aplicada:', rotation);

    let adjustedY = posData.rotation
      ? Rheight - 100 * Math.sin(rotation * (Math.PI / 180))
      : Rheight;

    // Ajustar si la firma se saldría por arriba o por abajo de la página
    // (igual que el clamping de X que ya existía)
    const signatureH = 65;
    if (adjustedY < 0) adjustedY = 0;
    if (adjustedY + signatureH > pageHeight) adjustedY = pageHeight - signatureH;

    // Crear un nombre único para el XObject usando userId y pageIndex
    const xObjectName = PDFName.of(`Signature_${userId}_${pageIndex}`);
    console.log('XObject Name:', xObjectName.encodedName);

    // Obtener o inicializar los recursos de la página
    let pageResources = selectedPage.node.Resources();
    if (!pageResources) {
      console.warn('Recursos de la página no definidos. Inicializando...');
      pageResources = pdfDoc.context.obj({});
      selectedPage.node.set(PDFName.of('Resources'), pageResources);
    }
    if (typeof pageResources.get !== 'function' || typeof pageResources.set !== 'function') {
      console.error('Los recursos de la página no son válidos:', pageResources);
      throw new Error('Los recursos de la página no son válidos.');
    }

    // Obtener o inicializar el diccionario de XObjects
    let xObjects = pageResources.get(PDFName.of('XObject'));
    
    // NUEVO: Si xObjects es una referencia, la resolvemos para obtener el objeto real
    if (xObjects instanceof PDFRef) {
      xObjects = pdfDoc.context.lookup(xObjects);
    }

    if (!xObjects || typeof xObjects.set !== 'function') {
      console.warn('XObjects no definidos en los recursos. Inicializando...');
      xObjects = pdfDoc.context.obj({});
      pageResources.set(PDFName.of('XObject'), xObjects);
    }
    if (typeof xObjects.set !== 'function') {
      console.error('xObjects no tiene método set:', xObjects);
      throw new Error('Error al inicializar los XObjects.');
    }

    // Agregar el nuevo XObject al diccionario con el nombre único
    xObjects.set(xObjectName, signatureImageEmbed.ref);

    // Forzar la conversión y formateo de los valores numéricos
    const posX = Number((adjustedRwidth + offsetX).toFixed(2));
    const posY = Number(adjustedY.toFixed(2));
    const posXFinal = Number((posX + signatureBBoxWidth).toFixed(2));
    const posYFinal = Number((adjustedY + 65).toFixed(2));

    if (!Number.isFinite(posX) || !Number.isFinite(posY) ||
        !Number.isFinite(posXFinal) || !Number.isFinite(posYFinal)) {
      throw new Error('Valores no válidos para BBox');
    }

    // Definir el BBox para el XObject
    const bbox = pdfDoc.context.obj([posX, posY, posXFinal, posYFinal]);

    if (signatureImageEmbed.dict && typeof signatureImageEmbed.dict.set === 'function') {
      signatureImageEmbed.dict.set(PDFName.of('BBox'), bbox);
      console.log('BBox definido en el XObject:', {
        xMin: adjustedRwidth + offsetX,
        yMin: adjustedY,
        xMax: adjustedRwidth + offsetX + signatureBBoxWidth,
        yMax: adjustedY + 65
      });
    } else {
      // Si el diccionario no es válido, reconfigurar manualmente el XObject
      const imageDict = pdfDoc.context.obj({
        Type: PDFName.of('XObject'),
        Subtype: PDFName.of('Image'),
        Width: 200,
        Height: 65,
        BBox: bbox
      });
      xObjects.set(xObjectName, imageDict);
      console.warn('Se reconfiguró manualmente el diccionario del XObject.');
    }

    const signatureWidth = 190; // Ancho ajustado de la firma
    const signatureHeight = 60; // Altura ajustada de la firma

    // Dibujar la imagen de la firma en la página
    selectedPage.drawImage(signatureImageEmbed, {
      x: adjustedRwidth + offsetX,
      y: adjustedY,
      width: signatureWidth,
      height: signatureHeight,
      rotate: degrees(rotation)
    });

    console.log('Firma gráfica incrustada en:', {
      x: adjustedRwidth + offsetX,
      y: adjustedY,
      width: 200,
      height: 65
    });

    let textOffsetX = Rwidth + signatureWidth + 10;
    let currentY = adjustedY + (signatureHeight / 2);

    // Información común (ubicación, país, fecha) 
    const locationText = country.latitude && country.longitude
      ? `${parseFloat(country.latitude).toFixed(6)}, ${parseFloat(country.longitude).toFixed(6)}`
      : '';

    if (userExist) {
      textOffsetX = Rwidth + signatureWidth - 20;
      currentY = adjustedY + signatureHeight;
      if (locationText) {
        selectedPage.drawText(locationText, { x: textOffsetX, y: currentY, size: 9 });
        currentY -= 12;
      }
      if (country?.country) {
        selectedPage.drawText(country.country, { x: textOffsetX, y: currentY, size: 9 });
        currentY -= 12;
      }
      selectedPage.drawText(date, { x: textOffsetX, y: currentY, size: 9 });
    } else {
      // Dibujar ícono, información y enlace si el usuario no tiene cuenta
      // (iconImageEmbed ya fue descargado e incrustado una sola vez antes del loop)
      const iconWidth = 35;
      const iconHeight = 35;
      const iconX = Rwidth + 10;
      const iconY = adjustedY - iconHeight - 10;
      selectedPage.drawImage(iconImageEmbed, { x: iconX, y: iconY, width: iconWidth, height: iconHeight });
      console.log('Ícono ajustado e incrustado en:', { x: iconX, y: iconY, width: iconWidth, height: iconHeight });
      textOffsetX = iconX + iconWidth + 10;
      currentY = iconY + iconHeight - 5;
      if (locationText) {
        selectedPage.drawText(locationText, { x: textOffsetX, y: currentY, size: 9 });
        currentY -= 12;
      }
      if (country?.country) {
        selectedPage.drawText(country.country, { x: textOffsetX, y: currentY, size: 9 });
        currentY -= 12;
      }
      selectedPage.drawText(date, { x: textOffsetX, y: currentY, size: 9 });
      currentY -= 12;
      selectedPage.drawText('Firmado en:', { x: textOffsetX, y: currentY, size: 9, color: rgb(0, 0, 0) });
      const linkText = 'https://signlydocs.com';
      selectedPage.drawText(linkText, { x: textOffsetX + 52, y: currentY, size: 9, color: rgb(0, 0, 1) });
      const annotations = selectedPage.node.Annots() || pdfDoc.context.obj([]);
      selectedPage.node.set(PDFName.of('Annots'), annotations);
      annotations.push(pdfDoc.context.obj({
        Type: PDFName.of('Annot'),
        Subtype: PDFName.of('Link'),
        Rect: [textOffsetX + 60, currentY - 2, textOffsetX + 175, currentY + 8],
        Border: [0, 0, 0],
        A: pdfDoc.context.obj({ Type: PDFName.of('Action'), S: PDFName.of('URI'), URI: linkText })
      }));
    }

    // Dibujar los metadatos (firma, página, posición, etc.)
    // Van en la esquina inferior izquierda de la parte VISIBLE de la hoja, no en (0, 0): hay PDFs cuya caja de
    // página (CropBox/MediaBox) no empieza en el origen (p. ej. x=-11.5, y=11.5). Ahí (0, 0) queda fuera de la hoja,
    // el servidor no extrae ese texto y no encuentra los metadatos (408 "Metadatos de firma gráfica no encontrados").
    const visibleBox = selectedPage.getCropBox();
    selectedPage.drawText(
      `SIGN_ID:${userId};PAGE:${pageIndex};POS_X:${(adjustedRwidth + offsetX).toFixed(2)};POS_Y:${adjustedY.toFixed(2)};WIDTH:${signatureBBoxWidth};HEIGHT:65`,
      { x: visibleBox.x + 1, y: visibleBox.y + 1, size: 1 }
    );
  }

  const modifiedPdfBytes = await pdfDoc.save();
  return new File([modifiedPdfBytes], fileName, { type: 'application/pdf' });
};

export const cancelAllTours = () => {
  localStorage.setItem('tourCompleted', 'true');
  localStorage.setItem('stepTypeSignTourCompleted', 'true');
  localStorage.setItem('stepWhoSignTourCompleted', 'true');
  localStorage.setItem('modalInviteTourCompleted', 'true');
  localStorage.setItem('withInvitesTourCompleted', 'true');
  localStorage.setItem('withInvitesAdvancedTourCompleted', 'true');
  localStorage.setItem('startAddSignaturesLinesTour', 'true');
  localStorage.setItem('addSignaturesLinesTourCompleted', 'true');
  localStorage.setItem('secondTourWithInvitesSingleCompleted', 'true');
  localStorage.setItem('secondTourWithInvitesAdvancedCompleted', 'true');
  localStorage.setItem('selectSignatureLineTourCompleted', 'true');
  localStorage.setItem('myselfAdvancedSignTourCompleted', 'true');
  localStorage.setItem('mySelfSingleNoLinesTourCompleted', 'true');
  localStorage.setItem('mySelfSingleIgnoreTourCompleted', 'true');
  localStorage.setItem('withInvitesNoLinesTourCompleted', 'true');
  localStorage.setItem('withInvitesIgnoreTourCompleted', 'true');
  localStorage.setItem('sidebarSharedTourCompleted', 'true');
  localStorage.setItem('controlAddLinesTourCompleted', 'true');
  localStorage.setItem('controlAddSignatureTourCompleted', 'true');

  console.log('Todos los tours han sido cancelados.');
};

export const handleTourAbort = (driverObj, tourKey, onComplete) => {
  import('sweetalert2').then(({ default: Swal }) => {
    Swal.fire({
      title: '¿Omitir ayuda interactiva?',
      text: '¿Deseas desactivar todos los tours? Puedes reactivarlos después desde tu perfil.',
      icon: 'question',
      showCancelButton: true,
      // Contraste suficiente con el texto blanco (antes #4A72B2 / #84a5c0 se leían mal).
      confirmButtonColor: '#285FF6',
      cancelButtonColor: '#4B5563',
      confirmButtonText: 'Sí, desactivar todos',
      cancelButtonText: 'No, solo cerrar este',
      // El tour se oculta mientras el aviso está abierto (ver index.css, .tour-abort-open)
      didOpen: () => document.documentElement.classList.add('tour-abort-open'),
      willClose: () => document.documentElement.classList.remove('tour-abort-open')
    }).then((result) => {
      if (result.isConfirmed) {
        cancelAllTours();
      } else {
        if (tourKey) localStorage.setItem(tourKey, 'true');
      }
      driverObj.destroy();
      if (onComplete) onComplete();
    });
  });
};

export const resetTours = (onReset) => {
  // Limpiar los valores almacenados en localStorage
  localStorage.removeItem('tourCompleted'); // Home.jsx
  localStorage.removeItem('stepTypeSignTourCompleted'); // StepTypeSign.jsx
  localStorage.removeItem('stepWhoSignTourCompleted'); // StepWhoSign.jsx
  localStorage.removeItem('modalInviteTourCompleted'); // ModalInvite.jsx
  localStorage.removeItem('withInvitesTourCompleted'); // WithInvitesSingle.jsx
  localStorage.removeItem('withInvitesAdvancedTourCompleted'); // WithInvitesAdavanced.jsx
  localStorage.removeItem('startAddSignaturesLinesTour'); // WithInvitesSingle.jsx
  localStorage.removeItem('addSignaturesLinesTourCompleted'); // AddSignaturesLines.jsx
  localStorage.removeItem('secondTourWithInvitesSingleCompleted'); // Tour para AllowDownload
  localStorage.removeItem('secondTourWithInvitesAdvancedCompleted'); // Tour para AllowDownload en avanzadas
  localStorage.removeItem('selectSignatureLineTourCompleted');
  localStorage.removeItem('myselfAdvancedSignTourCompleted'); // Tour para "Firmaré solo yo" -> Avanzadas (FAB/FEA)
  localStorage.removeItem('mySelfSingleNoLinesTourCompleted'); // Tour para "Firmaré solo yo" -> "No tiene líneas"
  localStorage.removeItem('mySelfSingleIgnoreTourCompleted'); // Tour para "Firmaré solo yo" -> "Solo me interesa firmar"
  localStorage.removeItem('withInvitesNoLinesTourCompleted'); // Tour para "Yo e invitados" -> "No tiene líneas"
  localStorage.removeItem('withInvitesIgnoreTourCompleted'); // Tour para "Yo e invitados" -> "Solo me interesa firmar"
  localStorage.removeItem('controlAddLinesTourCompleted'); // Tour para agregar líneas
  localStorage.removeItem('controlAddSignatureTourCompleted'); // Tour para agregar firmas

  console.log('Tours reiniciados. Ahora pueden iniciarse de nuevo.');

  // Ejecutar callback si está definido
  if (typeof onReset === 'function') {
    onReset();
  }
};

export const extractOwner = (participants = [], owner) => {
  const exist = participants.some((p) => p.email === owner);
  return exist;
};

export const validateRFC = (rfc = '') => {
  if (rfc.length > 0) {
    const rfcRegex = /^[A-Z]{4}\d{6}[A-Z\d]{3}$/;
    const isValid = rfc.match(rfcRegex);
    if (isValid) {
      return { valid: true, msg: '' };
    }
    return { valid: false, msg: 'RFC_invalid' };
  } else {
    return { valid: false, msg: 'RFC_required' };
  }
};

export const getAdjust = (size) => {
  return size / 10.9;
};

export const transformSigns = (sign) => {
  // const base64 = btoa(String.fromCharCode(...new Uint8Array(sign.img.data)));
  // const dataUrl = `data:image/png;base64,${base64}`;
  return { name: sign.name, img: sign.img, id: sign.id };
};

export const indexValues = (values, key) => {
  const newValues = values.reduce((acc, el) => ({ ...acc, [el[key]]: el }), {});
  return newValues;
};

export const validateExistPosition = (positions, participants) => {
  const existAtograhp = participants.some((p) => p.typeofSign === typesSign.FAB);
  if (existAtograhp) {
    if (!Object.keys(positions).length > 0) {
      return false;
    }
  }
  return true;
};

export const validateExistPositionAndSign = (
  positions,
  participants,
  documentInfo
) => {
  const existAtograhp = participants.some((p) => p.typeofSign == typesSign.FAB);
  if (existAtograhp) {
    if (!Object.keys(positions).length > 0) {
      return false;
    }
  } else if (documentInfo.owner_sign) {
    if (
      documentInfo.typeOfSignOwner === 0 ||
      documentInfo.typeOfSignOwner === false
    ) {
      if (!Object.keys(positions).length > 0) {
        return false;
      }
    }
  }
  return true;
};

export const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

export const getSignersByType = (signers) => {
  const signersFiel = signers.filter(
    ({ typeofSign }) => typeofSign === typesSign.FEA
  );
  const signersAutographBio = signers.filter(
    ({ typeofSign }) => typeofSign === typesSign.FAB
  );
  const signersAutograph = signers.filter(
    ({ typeofSign }) => typeofSign === typesSign.AUTOGRAPH
  );
  return { signersFiel, signersAutograph, signersAutographBio };
};

export const decodeURL = (encodedParams) => {
  if (!encodedParams) return null;
  // Si tiene puntos, asumimos que es un JWT (Header.Payload.Signature)
  if (encodedParams.includes('.')) {
    try {
      const payloadBase64 = encodedParams.split('.')[1];
      const payloadString = atob(payloadBase64);
      return JSON.parse(payloadString);
    } catch (e) {
      console.error('Error decodificando payload JWT:', e);
      return null;
    }
  } else {
    // Fallback a Base64 antiguo
    try {
      const paramsString = atob(encodedParams);
      return JSON.parse(paramsString);
    } catch (e) {
      console.error('Error decodificando payload Base64:', e);
      return null;
    }
  }
};

export const transformDate = (fechaISO) => {
  const userTimezone = dayjs.tz.guess(); // Detectar automáticamente zona horaria del usuario
  const d = dayjs(fechaISO).tz(userTimezone);
  // Formato compacto en 3 líneas (día / mes abreviado / año), pensado para
  // la columna "Fecha" de las tablas de documentos; el contenedor debe
  // tener white-space: pre-line para que los \n se vean como saltos de línea.
  const day = d.format('D');
  const month = d.format('MMM');
  const monthCapitalized = month.charAt(0).toUpperCase() + month.slice(1);
  const year = d.format('YYYY');
  return `${day}\n${monthCapitalized}\n${year}`;
};

export const addPrefixToDocument = (title) => {
  // caso defensivo: no viene título
  if (!title || typeof title !== 'string') {
    return 'document_signed_by_Signly.pdf';
  }

  const dotIndex = title.lastIndexOf('.');
  const base = dotIndex === -1 ? title : title.slice(0, dotIndex);
  const ext = dotIndex === -1 ? '' : title.slice(dotIndex); // conserva extensión original

  return `${base}_signed_by_Signly${ext}`;
};

export const verifyExistFABFEAInvites = (participants) => {
  const exist = participants.some(
    ({ typeofSign }) =>
      typeofSign === typesSign.FAB || typeofSign === typesSign.FEA
  );
  return exist;
};

export const agroupedSigners = (participants) => {
  const usuariosAgrupados = {};

  participants.forEach((usuario) => {
    const email = usuario.email;
    if (usuariosAgrupados[email]) {
      usuariosAgrupados[email].push(usuario);
    } else {
      usuariosAgrupados[email] = [usuario];
    }
  });

  const resultadoFinal = [];
  for (const email in usuariosAgrupados) {
    const usuariosConEsteEmail = usuariosAgrupados[email];
    resultadoFinal.push(usuariosConEsteEmail);
  }
  return resultadoFinal;
};

export const difText = (textBase, newText) => {
  const addedCharactes = Math.abs(newText.length - textBase.length);
  if (addedCharactes > 0) {
    return { ok: false, msg: 'cannot_add_letters' };
  }

  let modifiedCount = 0;
  for (let i = 0; i < textBase.length; i++) {
    if (textBase[i] !== newText[i]) {
      modifiedCount++;
    }
  }
  if (modifiedCount > 3) {
    return { ok: false, msg: 'many_modifications' };
  }

  return { ok: true, msg: '' };
};

export const validateOS = (userAgent) => {
  let operatingSystem = 'Desconocido';

  if (userAgent.includes('Windows')) {
    operatingSystem = 'Windows';
  } else if (userAgent.includes('Macintosh') || userAgent.includes('Mac OS')) {
    operatingSystem = 'macOS';
  } else if (userAgent.includes('Linux')) {
    operatingSystem = 'Linux';
  } else if (userAgent.includes('Android')) {
    operatingSystem = 'Android';
  } else if (userAgent.includes('iOS')) {
    operatingSystem = 'iOS';
  }

  return operatingSystem;
};

export const getTimestampFromRangePicker = (initialDate, endDate) => {
  // limpio cadenas y creo Date
  const initialClean = initialDate.replace(/\([^()]*\)/g, '').trim();
  const endClean = endDate.replace(/\([^()]*\)/g, '').trim();
  const start = new Date(initialClean);
  const end = new Date(endClean);

  // <-- aquí la clave: extiendo hasta fin de día
  end.setHours(23, 59, 59, 999);

  return {
    initialDate: start.getTime(),
    endDate: end.getTime()
  };
};

export const validatePassword = (password) => {
  const passwordRegex =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
  return passwordRegex.test(password);
};

// export const logOutActions = () => {
//   localStorage.removeItem('jwt');
//   sessionStorage.removeItem('isAuth');
//   localStorage.removeItem('userId');
//   localStorage.removeItem('profile');
//   localStorage.removeItem('userInfo');
//   localStorage.removeItem('test');
//   localStorage.removeItem('phone');
//   localStorage.removeItem('allowDownload');
//   localStorage.removeItem('isRegularAccess');
//   localStorage.removeItem('paypal_token');
//   localStorage.removeItem('allowView');
//   // Eliminar la cookie 'token'
//   Cookies.remove('token', {
//     domain: 'signly.kiubix.biz',
//     path: '/',
//     secure: true,
//     sameSite: 'Strict'
//   });
//   Cookies.remove('refreshToken', {
//     domain: 'signly.kiubix.biz',
//     path: '/',
//     secure: true,
//     sameSite: 'Strict'
//   });
//   sessionStorage.removeItem('tokenInvite');
//   window.location.href = '/login';
//   googleLogout();
// };

import { disconnectSocket } from '@/socket';
import { Sentry } from '@/sentry';

export const logOutActions = async (navigate = null, options = {}) => {
  const performCleanup = () => {
    // Contraparte del Sentry.setUser() en App.jsx (PrivateRoutes): si no se
    // limpia aquí, un error que ocurra ya deslogueado seguiría atribuyéndose
    // al último usuario que inició sesión en esta pestaña.
    Sentry.setUser(null);
    localStorage.removeItem('jwt');
    sessionStorage.removeItem('isAuth');
    localStorage.removeItem('userId');
    localStorage.removeItem('profile');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('test');
    localStorage.removeItem('phone');
    localStorage.removeItem('allowDownload');
    localStorage.removeItem('isRegularAccess');
    localStorage.removeItem('paypal_token');
    localStorage.removeItem('allowView');
    sessionStorage.removeItem('tokenInvite');
    localStorage.removeItem('tokenExp');
    // Si esta pestaña se desloguea solo porque perdió la carrera del refresh
    // (otra sesión ya es la válida), no se borra `globalSessionId`: hacerlo
    // dispararía el aviso de "otra sesión" en la pestaña ganadora también,
    // dejando a ambas desconectadas en vez de solo a la que debía perder.
    if (!options.preserveGlobalSession) {
      localStorage.removeItem('globalSessionId');
      localStorage.removeItem('authSessionId');
    }

    // Limpiar cualquier notificación de invitado persistente de localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('signlyGuestNotification_') || key === 'signlyGuestNotification') {
        localStorage.removeItem(key);
      }
    });

    googleLogout();
    disconnectSocket('logout');
  };

  try {
    // Realiza la petición al endpoint de logout en el backend.
    // Se omite cuando otra sesión ya reemplazó a esta (otra pestaña de este
    // navegador o la sesión vigente comparten la cookie): el logout borraría
    // las cookies y revocaría la sesión que SÍ es válida.
    if (!options.skipServerLogout) {
      await axios.post(`${process.env.REACT_APP_BASE_URL}auth/logout`, {}, { withCredentials: true });
    }
  } catch (error) {
    console.error('Error durante el logout:', error);
  } finally {
    performCleanup();
    if (navigate) {
      navigate('/login');
    } else {
      window.location.href = '/login';
    }
  }
};

const cleanText = (text) => {
  return text
    .replace(/[^\w\s,.]/g, '') // Elimina todo excepto letras, números, espacios, comas y puntos
    .replace(/\s{2,}/g, ' ') // Sustituye múltiples espacios por uno solo
    .trim(); // Elimina espacios al inicio y final
};

const truncateText = (text, maxLength) => {
  const words = text.split(/\s+/);
  return words.slice(0, maxLength).join(' '); // Limita el texto al número máximo de palabras
};

// Helpers
export const extractTextFromDocuments = async(pdf, pageNumber) => {
  const typedArray = new Uint8Array(pdf);
  const doc = await pdfjs.getDocument({ data: typedArray, ...PDFJS_SAFE_OPTIONS }).promise;

  console.log(`El documento tiene ${doc.numPages} páginas.`); // Verificación del número total de páginas

  if (pageNumber > doc.numPages || pageNumber < 1) {
    console.error(`Número de página ${pageNumber} fuera de rango.`);
    return null;
  }

  const page = await doc.getPage(pageNumber);
  const content = await page.getTextContent();
  const rawText = content.items.map((item) => item.str).join(' ');

  // Aplicar sanitización avanzada
  const sanitizedText = cleanText(rawText);

  if (!sanitizedText || sanitizedText.length < 10) {
    throw new Error('El texto extraído es demasiado corto o inválido.');
  }

  if (sanitizedText.split(/\s+/).length > 3000) {
    throw new Error('El texto extraído excede el límite de tokens permitido.');
  }

  // Reducir el tamaño del texto si es necesario
  const truncatedText = truncateText(sanitizedText, 300); // Cambia 300 por el número de palabras que quieras limitar

  return truncatedText;
};

export const isSigned = (positions) => {
  // Iterar sobre todos los valores de cada página
  for (const pagePositions of Object.values(positions)) {
    // Comprobar si alguna posición en la página es de tipo 'signature' y está marcada como firmada
    if (pagePositions.some(pos => pos.type === 'signature' && pos.signed)) {
      return true; // Si encuentra al menos una, retorna verdadero
    }
  }
  return false; // Si no encuentra ninguna, retorna falso
};

export * from './locationHelper';

